/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import axios from 'axios';
import VideoPlayer from "./VideoPlayer";
import VideoPlaylist from "./VideoPlaylist";
import 'videojs-playlist-ui';
import 'videojs-playlist-ui/dist/videojs-playlist-ui.vertical.css';
import {Byline} from "content_src/components";
import {ShareButtons} from "anvil_social_src/components";


const VIDEO_API_ENDPOINT = '/api/v1/swagpages/';
/**
 * Displays a video with a playlist sidebar to control what plays next.
 */
const VideoGallery = ({sectionList, dfpTag, sassPrefix}) => {
    const videoSectionNames = useMemo(() => sectionList.map((playlist) => playlist.title), [sectionList]);
    const [loading, setLoading] = useState(false);
    const [video, setVideo] = useState({});
    const [videoOffset, setVideoOffset] = useState(0);
    const [loadMoreVideos, setLoadMoreVideos] = useState(true);
    const [videoSectionsIndex, setVideoSectionsIndex] = useState(0);

    // Reference to the video player instance
    const galleryEle = useRef(null);
    const videojs = useRef(null);
    const [galleryHeight, setGalleryHeight] = useState('100vh');

    // Handle video.js event when the next video begins playing
    const handleNextItemPlayEvent = useCallback((event, video) => {
        const player = videojs.current.playerRef.current;

        try {
            if (player.ima && player.ads._seenInitialLoadstart && player.ads._playRequested && !player.ads.inAdBreak()) {
                player.ima.changeAdTag(dfpTag);
                player.ima.initializeAdDisplayContainer();
                player.ima.requestAds();
            }
        } catch (e) {
            console.warn('Unable to load ads for video, this is often caused by an ad block software. Exception:', e);
        }
        // Add video information below the player
        setVideo(video);
    }, [dfpTag]);

    // Initialize the video.js player, only done once
    useEffect(() => {
        if (videojs.current && videojs.current.playerRef.current) {
            const player = videojs.current.playerRef.current;
            player.playlist([]);
            player.playlist.autoadvance(2);
            player.on('beforeplaylistitem', handleNextItemPlayEvent);
            player.playlistUi();

            // Cleanup listener
            return () => player.off('beforeplaylistitem', handleNextItemPlayEvent);
        }
    }, [handleNextItemPlayEvent, videojs]);

    // Dynamically set the min-height of the gallery so that it is always contained inside the viewport.
    useEffect(() => {
        if (galleryEle.current) {
            const updateHeight = () => {
                const galleryElement = galleryEle.current;
                // Calculate how far from the top of the viewport the gallery is
                const {top: topOffset} = galleryElement.getBoundingClientRect();
                // Add a buffer of 15px so that it doesn't sit on the bottom of the screen
                const totalOffset = topOffset + 15;
                setGalleryHeight(`calc(100vh - ${totalOffset}px)`);
            };
            document.addEventListener('DOMContentLoaded', updateHeight);
            // Cleanup the dom listener
            return () => document.removeEventListener('DOMContentLoaded', updateHeight);
        }
    }, [galleryEle]);

    const loadVideos = useCallback((sectionId = null, resetVideos = false) => {
        const player = videojs.current.playerRef.current;

        setLoading(true);

        // If sectionId not provided, default to load more videos for the current section.
        if (sectionId === null) {
            sectionId = sectionList[videoSectionsIndex].id;
        }

        let offset = videoOffset;
        // Handle resetting the state back to initial values when we are switching playlists entirely
        if (resetVideos === true) {
            setVideoOffset(0);
            offset = 0;
            player.playlist([], 0);
        }

        // Async function to load the videos from the API.
        const asyncLoad = async () => {
            const videoPageFields = [
                'title',
                'media_item',
                'first_published_at',
                'content_updated_at',
                'author',
                'publication',
                'full_url',
            ];
            const response = await axios.get(
                VIDEO_API_ENDPOINT,
                {
                    params: {
                        member_of: sectionId,
                        type: 'swagmedia.VideoPage',
                        fields: videoPageFields.join(','),
                        offset,
                    }
                }
            );

            // Keep track of how far we have loaded in the videos, used to grab the next set of videos
            // as we scroll
            const numberOfPages = response.data.pages.length;
            setVideoOffset((prevOffset) => prevOffset + numberOfPages);

            // Determine if we have loaded all of the videos
            const totalPages = response.data.meta.total_count;
            setLoadMoreVideos((offset + numberOfPages) < totalPages);

            // Convert the video pages to a format our components can read
            const videoItems = response.data.pages.map((page) => {
                const videoPageData = {
                    name: page.title,
                    thumbnail: page.media_item.thumbnail.image_sizes.large,
                    author: page.author,
                    publication: page.publication,
                    duration: page.media_item.duration,
                    firstPublishedAt: page.first_published_at,
                    contentUpdatedAt: page.content_updated_at,
                    fullUrl: page.full_url,
                };
                videoPageData.sources = page.media_item.renditions.map((mediaItem) => ({
                    src: mediaItem.file,
                    type: mediaItem.mime_type
                }));
                return videoPageData;
            });

            if (resetVideos) {
                // Update the video.js playlist, and reset the playlist position
                player.playlist(videoItems);
                player.playlist.currentItem(0);
            } else {
                // Update the video.js playlist, and keep the existing the playlist position
                player.playlist([...player.playlist(), ...videoItems], -1);
            }

            setLoading(false);
        };
        asyncLoad();
    }, [videoOffset, videojs, videoSectionsIndex, sectionList]);

    // Load the videos when the component renders initially and when we switch playlists
    useEffect(() => {
        loadVideos(sectionList[videoSectionsIndex].id, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionList, videoSectionsIndex]);

    // User has selected a different channel to play from.
    const onPlaylistChange = useCallback((playlistIndex) => {
        setVideoSectionsIndex(playlistIndex);
    }, []);

    // Load more videos when we reach the bottom of the list
    const onEndReached = useCallback(() => {
        loadVideos();
    }, [loadVideos]);

    return (
        <div className="video-gallery" style={{height: galleryHeight}} ref={galleryEle}>
            <div className="video-gallery__player">
                <VideoPlayer ref={videojs} fluid={true} dfpTag={dfpTag}
                             videojsClasses={'video-js vjs-big-play-centered vjs-16-9'}
                             autoplay />
                <div className="video-gallery__video-info">
                    <div className="row expanded">
                        <div className="column align-middle small-8 medium-10">
                            <h2 className="video-gallery__title">
                                <a href={video.fullUrl} title={video.fullUrl}>
                                    {video.name ? video.name : ''}
                                </a>
                            </h2>
                        </div>
                        <div className="video-gallery__share column small-4 medium-2">
                            <ShareButtons url={video.fullUrl} sassPrefix={sassPrefix}/>
                        </div>
                    </div>
                    <div className="row expanded">
                        <div className="video-gallery__byline column small-12">
                            <Byline updatedDate={video.contentUpdatedAt}
                                    publishedDate={video.firstPublishedAt}
                                    authorName={video.author ? video.author.full_name : ''}
                                    profilePicture={(!!video.author && !!video.author.profile_picture) ? video.author.profile_picture.image_sizes.medium : ''}
                                    twitterHandle={video.author ? video.author.twitter_handle : ''}
                                    sassPrefix={sassPrefix}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="video-gallery__playlist">
                <VideoPlaylist playlistNames={videoSectionNames}
                               onPlaylistChange={onPlaylistChange} isLoading={loading} onEndReached={onEndReached}
                               enableInfiniteScrolling={loadMoreVideos}/>
            </div>
        </div>
    );
};

export default VideoGallery;
